import { Injectable, Signal, inject } from '@angular/core';

import { makeItemId } from '../functions/dashboard-chart.functions';
import type { DashboardCardConfig, DashboardPreset } from '../types';
import { UserPreferencesService } from './user-preferences.service';

@Injectable({ providedIn: 'root' })
export class DashboardPresetsService {
  private readonly prefs = inject(UserPreferencesService);

  readonly presets: Signal<ReadonlyArray<DashboardPreset>> = this.prefs.select('dashboardPresets');

  save(entry: {
    name: string;
    description?: string;
    config: DashboardCardConfig;
    colSpan: number;
    rowSpan: number;
  }): string {
    const id = makeItemId();
    const preset: DashboardPreset = {
      id,
      name: entry.name,
      ...(entry.description ? { description: entry.description } : {}),
      config: entry.config,
      colSpan: entry.colSpan,
      rowSpan: entry.rowSpan,
    };
    this.prefs.set('dashboardPresets', [...this.prefs.get('dashboardPresets'), preset]);
    return id;
  }

  update(
    id: string,
    entry: {
      name: string;
      description?: string;
      config: DashboardCardConfig;
      colSpan: number;
      rowSpan: number;
    },
  ): void {
    const preset: DashboardPreset = {
      id,
      name: entry.name,
      ...(entry.description ? { description: entry.description } : {}),
      config: entry.config,
      colSpan: entry.colSpan,
      rowSpan: entry.rowSpan,
    };
    const newConfig: DashboardCardConfig = { ...entry.config, presetId: id };
    this.prefs.set(
      'dashboardPresets',
      this.prefs.get('dashboardPresets').map((p) => (p.id === id ? preset : p)),
    );
    this.prefs.set(
      'dashboardLayout',
      this.prefs.get('dashboardLayout').map((w) =>
        w.config.presetId === id ? { ...w, config: newConfig } : w,
      ),
    );
    this.prefs.set(
      'reports',
      this.prefs.get('reports').map((r) => ({
        ...r,
        widgets: r.widgets.map((w) =>
          w.config.presetId === id ? { ...w, config: newConfig } : w,
        ),
      })),
    );
  }

  rename(id: string, name: string, description: string | undefined): void {
    this.prefs.set(
      'dashboardPresets',
      this.prefs.get('dashboardPresets').map((p) => {
        if (p.id !== id) return p;
        const next: DashboardPreset = { ...p, name };
        if (description) return { ...next, description };
        const { description: _d, ...rest } = next;
        return rest as DashboardPreset;
      }),
    );
  }

  delete(id: string): void {
    this.prefs.set(
      'dashboardPresets',
      this.prefs.get('dashboardPresets').filter((p) => p.id !== id),
    );
  }
}
